import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { FileText, Printer, X } from 'lucide-react';
import { formatCATDate } from '../../utils/timezone';

export default function ClaimDetailsModal({ open, onOpenChange, claim, formatCurrency, toLocalAmount }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="w-6 h-6 text-purple-600" />
            Claim Details - {claim?.header.claim_id}
          </DialogTitle>
        </DialogHeader>

        {claim && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-lg border border-purple-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Patient Name</p>
                  <p className="text-base font-semibold text-gray-800">{claim.header.patient_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-medium">Serial Number</p>
                  <p className="text-base font-semibold text-gray-800">{claim.header.patient_serial_number}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-medium">Family ID</p>
                  <p className="text-base font-semibold text-gray-800">{claim.header.family_id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-medium">Date</p>
                  <p className="text-base font-semibold text-gray-800">{formatCATDate(claim.header.timestamp)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-medium">Hospital</p>
                  <p className="text-base font-semibold text-gray-800">{claim.header.hospital_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-medium">Status</p>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    claim.header.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800'
                      : claim.header.status === 'PAID' ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {claim.header.status}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Claim Items ({claim.details.length})
              </h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Item ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Item Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase normal-case">UoM</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Quantity</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Unit Cost</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claim.details.map((item, index) => (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="px-4 py-3 text-sm text-gray-700">{item.item_id}</td>
                        <td className="px-4 py-3 text-sm text-gray-800">{item.item_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{item.uom || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-center">{item.quantity}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatCurrency(toLocalAmount(item.item_cost))}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-800 text-right">{formatCurrency(toLocalAmount(item.item_cost * item.quantity))}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t-2 border-gray-300">
                      <td colSpan="4" className="px-4 py-3 text-right text-sm font-bold text-gray-800">Total Claim Amount:</td>
                      <td className="px-4 py-3 text-right text-base font-bold text-green-600">{formatCurrency(toLocalAmount(claim.header.total_claim_amount))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button onClick={() => window.open(`/print/${claim.header.claim_id}`, '_blank')} className="bg-blue-600 hover:bg-blue-700">
                <Printer className="w-4 h-4 mr-2" />
                Print Claim
              </Button>
              <Button onClick={() => onOpenChange(false)} variant="outline">
                <X className="w-4 h-4 mr-2" />
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
